// Klawiatura PIN-u dziecka (patch-34): okienko nad ekranem „Kto dziś się uczy?”. Cztery kropki, duże cyfry (działa palcem na tablecie i telefonie),
// a na komputerze — także klawiatura fizyczna (cyfry, Backspace, Esc). Czwarta cyfra sama sprawdza PIN; zły PIN trzęsie kropkami i mówi, ile prób zostało.
// Napisy idą za przełącznikiem EN/PL; okienko przykrywa ekran, więc język zmienia się tylko przed jego otwarciem.
import { plPlural } from '../lib/plural.ts';
import { PIN_LENGTH } from '../lib/pinLocal.ts';
import type { ProfileInfo, VerifyResult } from '../lib/profilesApi.ts';
import type { Pair } from '../lib/i18n.ts';
import { avatarEl } from './avatars.ts';
import type { AppCtx } from './ctx.ts';
import { button, h, shake } from './dom.ts';
import { langOf, txAttr, txText } from './i18n.ts';
import { icon } from './kit.ts';

const T = {
  back: { en: 'Back', pl: 'Wróć' },
  del: { en: 'Delete the last digit', pl: 'Usuń ostatnią cyfrę' },
  dots: { en: 'PIN: digits typed so far', pl: 'PIN: wpisane cyfry' },
  hint: { en: 'Type your 4-digit PIN', pl: 'Wpisz swój PIN (4 cyfry)' },
  checking: { en: 'Checking…', pl: 'Sprawdzam…' },
} as const satisfies Record<string, Pair>;

/** Komunikat po złym PIN-ie; `left` = ile prób zostało (null = nieznane). */
export function wrongPinText(left: number | null, lang: 'en' | 'pl'): string {
  if (left === null) return lang === 'en' ? 'Wrong PIN. Try again.' : 'Zły PIN. Spróbuj jeszcze raz.';
  if (left <= 0) return lang === 'en' ? 'Wrong PIN. This profile is locked for a while.' : 'Zły PIN. Profil jest na chwilę zablokowany.';
  if (lang === 'en') return `Wrong PIN. ${left} ${left === 1 ? 'try' : 'tries'} left.`;
  return `Zły PIN. ${plPlural(left, 'Została', 'Zostały', 'Zostało')} ${left} ${plPlural(left, 'próba', 'próby', 'prób')}.`;
}

/** Komunikat dla dziecka po każdym wyniku sprawdzenia PIN-u poza „ok”. */
export function pinFailureText(r: Exclude<VerifyResult, { kind: 'ok' }>, lang: 'en' | 'pl'): string {
  switch (r.kind) {
    case 'bad':
      return wrongPinText(r.left, lang);
    case 'locked':
      return lang === 'en' ? `Too many wrong tries. Try again in about ${r.retryAfterMin} min, or ask a parent for help.` : `Za dużo błędnych prób. Spróbuj za ok. ${r.retryAfterMin} min albo poproś o pomoc rodzica.`;
    case 'offline':
      return lang === 'en' ? 'No internet. This PIN can only be checked online the first time.' : 'Brak internetu. Ten PIN trzeba pierwszy raz sprawdzić z połączeniem.';
    case 'unpaired':
      return lang === 'en' ? 'This device was logged out. Ask a parent to log in.' : 'To urządzenie zostało wylogowane. Poproś rodzica o zalogowanie.';
    case 'gone':
      return lang === 'en' ? 'This profile was removed by a parent.' : 'Ten profil został usunięty przez rodzica.';
    case 'error':
      return lang === 'en' ? 'Something went wrong. Try again in a moment.' : 'Coś poszło nie tak. Spróbuj za chwilę.';
  }
}

export interface PinPadOptions {
  profile: ProfileInfo;
  /** Sprawdza PIN (serwer albo skrót z pamięci przy braku internetu). */
  verify(pin: string): Promise<VerifyResult>;
  /** PIN dobry — wchodzimy do profilu. */
  onOk(): void;
  /** Profil zniknął albo urządzenie straciło dostęp — ekran startowy musi się przeliczyć. */
  onLost(): void;
}

export interface PinPad {
  el: HTMLElement;
  close(): void;
}

/** Okienko PIN-u dołączone do ekranu (znika razem z nim po zmianie ekranu); `close()` zamyka je ręcznie. */
export function openPinPad(ctx: AppCtx, opts: PinPadOptions): PinPad {
  let digits = '';
  let busy = false;
  let closed = false;

  const dots = Array.from({ length: PIN_LENGTH }, () => h('span', { class: 'pin-dot' }));
  const dotsRow = txAttr(ctx, h('div', { class: 'pin-dots', role: 'img', 'data-testid': 'pin-dots' }, ...dots), 'aria-label', T.dots);
  const message = h('p', { class: 'pin-message', role: 'status', 'aria-live': 'polite', 'data-testid': 'pin-message' });
  txText(ctx, message, T.hint);

  const paint = (): void => {
    dots.forEach((d, i) => d.classList.toggle('on', i < digits.length));
    dotsRow.dataset.count = String(digits.length);
  };
  const say = (text: string, bad = false): void => {
    message.textContent = text;
    message.classList.toggle('bad', bad);
  };

  const keys: HTMLButtonElement[] = [];
  const setBusy = (b: boolean): void => {
    busy = b;
    for (const k of keys) k.disabled = b;
  };

  async function submit(): Promise<void> {
    const pin = digits;
    setBusy(true);
    say(T.checking[langOf(ctx)]);
    const r = await opts.verify(pin);
    if (closed) return;
    if (r.kind === 'ok') {
      opts.onOk();
      return;
    }
    digits = '';
    paint();
    shake(dotsRow);
    say(pinFailureText(r, langOf(ctx)), true);
    if (r.kind === 'unpaired' || r.kind === 'gone') {
      setTimeout(() => {
        if (!closed) opts.onLost();
      }, 1800);
      return; // klawiatura zostaje zablokowana: zaraz ekran startowy przeliczy się od nowa
    }
    if (r.kind === 'locked') return; // blokada: dopóki trwa, każda próba byłaby odrzucona
    setBusy(false);
  }

  function press(d: string): void {
    if (busy || closed || digits.length >= PIN_LENGTH) return;
    digits += d;
    paint();
    if (message.classList.contains('bad')) {
      message.classList.remove('bad');
      txText(ctx, message, T.hint);
    }
    if (digits.length === PIN_LENGTH) void submit();
  }
  function erase(): void {
    if (busy || closed || digits === '') return;
    digits = digits.slice(0, -1);
    paint();
  }

  const key = (label: string): HTMLButtonElement => {
    const k = h('button', { type: 'button', class: 'pin-key', 'data-testid': `pin-key-${label}`, onclick: () => press(label) }, label);
    keys.push(k);
    return k;
  };
  const del = txAttr(ctx, h('button', { type: 'button', class: 'pin-key pin-del', 'data-testid': 'pin-del', onclick: erase }, icon('backspace')), 'aria-label', T.del);
  keys.push(del);
  const pad = h('div', { class: 'pin-keys', role: 'group' }, ...['1', '2', '3', '4', '5', '6', '7', '8', '9'].map(key), h('span', { class: 'pin-key spacer', 'aria-hidden': 'true' }), key('0'), del);

  const onKey = (e: Event): void => {
    const k = (e as KeyboardEvent).key;
    if (k >= '0' && k <= '9' && k.length === 1) press(k);
    else if (k === 'Backspace') erase();
    else if (k === 'Escape') close();
  };
  function close(): void {
    if (closed) return;
    closed = true;
    document.removeEventListener('keydown', onKey);
    el.remove();
  }

  const title = h('h2', { class: 'pin-title', 'data-testid': 'pin-title' });
  const paintTitle = (): void => {
    title.textContent = langOf(ctx) === 'en' ? `${opts.profile.name}, your PIN` : `${opts.profile.name}, Twój PIN`;
  };
  paintTitle();
  ctx.teacher?.onChange(() => {
    if (!closed) paintTitle();
  });
  const cancel = txText(ctx, button('', close, { class: 'btn link', testid: 'pin-cancel' }), T.back);
  const el = h('div', { class: 'pin-overlay', 'data-testid': 'pin-pad', role: 'dialog', 'aria-modal': 'true' },
    h('div', { class: 'pin-card' }, avatarEl(opts.profile, 'lg', ctx), title, dotsRow, message, pad, cancel),
  );
  txAttr(ctx, el, 'aria-label', { en: 'Enter your PIN', pl: 'Wpisz swój PIN' });
  document.addEventListener('keydown', onKey);
  ctx.root.append(el);
  paint();
  return { el, close };
}
